import { useModelStore } from '@/store';

/**
 * Snap a raw value onto a model parameter's allowed options.
 * Handles string/number mismatches ("4" vs 4) and nearest-numeric fallback.
 * Returns undefined when the value should be omitted (let backend defaults apply).
 */
export function snapParamValueToModel(paramDef, rawValue) {
  if (rawValue === undefined || rawValue === null || rawValue === '') return undefined;
  if (!paramDef) return rawValue;

  const opts = paramDef.validation?.options;
  if (!Array.isArray(opts) || opts.length === 0) return rawValue;

  const validValues = opts.map((o) => o.value);
  if (validValues.includes(rawValue)) return rawValue;

  const asString = String(rawValue);
  const stringMatch = validValues.find((v) => String(v) === asString);
  if (stringMatch !== undefined) return stringMatch;

  const num = parseFloat(asString);
  if (!Number.isNaN(num)) {
    return validValues.reduce((best, v) => {
      const vn = parseFloat(String(v));
      if (Number.isNaN(vn)) return best;
      const bestN = parseFloat(String(best));
      return Math.abs(vn - num) < Math.abs(bestN - num) ? v : best;
    }, validValues[0]);
  }

  return validValues[0];
}

/**
 * Build namespaced selectedParameters for an explore → generation handoff.
 * Only keeps params the target model actually defines, snapped to valid options
 * so stale explore-item values (e.g. duration "5"/"10") cannot trip constraint
 * validation on Veo (4/6/8) or other video models.
 */
export function buildExploreSelectedParameters(modelId, localParams = {}, base = {}) {
  const selected = { ...base };
  if (!modelId || !localParams || typeof localParams !== 'object') return selected;

  const model = useModelStore.getState().getModelById?.(modelId)
    || useModelStore.getState().getModelBySlug?.(modelId);
  const paramDefs = Array.isArray(model?.parameters) ? model.parameters : [];
  if (!paramDefs.length) return selected;

  const byName = new Map(
    paramDefs
      .filter((p) => p?.name && String(p.name).toLowerCase() !== 'prompt')
      .map((p) => [p.name, p]),
  );

  for (const [key, raw] of Object.entries(localParams)) {
    if (raw === undefined || raw === null || raw === '') continue;
    // Ignore sibling aliases we don't want to forward blindly.
    if (key === 'duration' && byName.has('seconds') && localParams.seconds != null) continue;
    const paramDef = byName.get(key) || (key === 'duration' ? byName.get('seconds') : null);
    if (!paramDef) continue;
    const targetName = byName.has(key) ? key : paramDef.name;
    const snapped = snapParamValueToModel(paramDef, raw);
    if (snapped === undefined) continue;
    selected[`${modelId}:${targetName}`] = snapped;
  }

  return selected;
}
